/**
 * `defer-debug-chat-persist` — request/response shapes for the
 * persisted Conversational Debug panel.
 *
 * The wire format is intentionally a 1:1 mirror of the in-memory
 * `DebugChatMessage` used by `packages/flow-builder/src/lib/debug-chat`
 * so the panel can hydrate without a translation layer. The only
 * server-only fields are `id` (BIGINT primary key) and `createdAt`
 * (ISO string), which the panel already has equivalents for.
 */

import { z } from 'zod';

export const DebugChatRoleSchema = z.enum(['user', 'assistant', 'system']);
export type DebugChatRole = z.infer<typeof DebugChatRoleSchema>;

export const DebugChatSourceSchema = z.enum(['deterministic', 'ai']);
export type DebugChatSource = z.infer<typeof DebugChatSourceSchema>;

/** One persisted message row. */
export const DebugChatMessageRowSchema = z.object({
  id: z.string(),
  stepId: z.string().min(1),
  flowId: z.string().nullable(),
  runId: z.string().nullable(),
  role: DebugChatRoleSchema,
  text: z.string().min(1).max(8000),
  source: DebugChatSourceSchema.nullable(),
  createdAt: z.string(),
});
export type DebugChatMessageRow = z.infer<typeof DebugChatMessageRowSchema>;

/** GET /api/debug-chat/messages?stepId=… */
export const ListDebugChatMessagesResponseSchema = z.object({
  stepId: z.string(),
  messages: z.array(DebugChatMessageRowSchema),
});
export type ListDebugChatMessagesResponse = z.infer<typeof ListDebugChatMessagesResponseSchema>;

/** POST /api/debug-chat/messages */
export const AppendDebugChatMessageInputSchema = z.object({
  stepId: z.string().min(1).max(200),
  flowId: z.string().max(200).optional().nullable(),
  runId: z.string().max(200).optional().nullable(),
  role: DebugChatRoleSchema,
  text: z.string().min(1).max(8000),
  source: DebugChatSourceSchema.optional().nullable(),
});
export type AppendDebugChatMessageInput = z.infer<typeof AppendDebugChatMessageInputSchema>;

export const AppendDebugChatMessageResponseSchema = z.object({
  ok: z.literal(true),
  message: DebugChatMessageRowSchema,
});
export type AppendDebugChatMessageResponse = z.infer<typeof AppendDebugChatMessageResponseSchema>;

/** DELETE /api/debug-chat/messages?stepId=… */
export const ClearDebugChatMessagesResponseSchema = z.object({
  ok: z.literal(true),
  removed: z.number().int().min(0),
});
export type ClearDebugChatMessagesResponse = z.infer<typeof ClearDebugChatMessagesResponseSchema>;
